/**
 * Shared SOURCE/BUY + WALMART/SELL image pair with glass badges and hover zoom.
 * Used by Quick Winner and Auto Sourcing Results cards.
 *
 * Customer-facing rule: this component NEVER renders a technical image-status
 * message. Missing media degrades silently through an ordered candidate list and
 * finally to a neutral product illustration. Image-state facts are exposed only
 * as data attributes for diagnostics.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { ShoppingCart } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProductSourceBrandSvg } from "@/components/product/ProductSourceIconBar";
import "./product-card.css";

/** Official yellow Walmart Spark (same asset as Analyzer brand marks). */
function WalmartSparkIcon({ className }) {
  return (
    <span className={cn("pc-walmart-spark", className)} aria-hidden="true">
      {ProductSourceBrandSvg.walmart}
    </span>
  );
}

/** Neutral merchandise illustration — never a substitute product photo. */
function NeutralProductIllustration({ tone }) {
  return (
    <svg
      className={cn("pc-image-illustration", tone === "sell" && "is-sell")}
      viewBox="0 0 64 64"
      role="img"
      aria-label="Product image unavailable"
      data-testid="product-card-image-placeholder"
    >
      <rect x="12" y="20" width="40" height="30" rx="4" />
      <path d="M12 28h40" />
      <path d="M24 20l4-8h8l4 8" />
      <circle cx="32" cy="38" r="5" />
    </svg>
  );
}

function useHoverZoom(enabled) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const canHover =
    typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

  const onMove = useCallback((e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
    setPos({ x, y });
  }, []);

  return {
    ref,
    active: enabled && canHover && active,
    pos,
    handlers: enabled && canHover
      ? {
          onMouseEnter: () => setActive(true),
          onMouseLeave: () => setActive(false),
          onMouseMove: onMove,
        }
      : {},
  };
}

function GlassBadge({ tone, children, title }) {
  return (
    <span className={cn("pc-glass-badge", tone === "sell" ? "pc-glass-sell" : "pc-glass-buy")} title={title}>
      {children}
    </span>
  );
}

/** Ordered, deduplicated, http(s)-only image candidates. */
export function imageCandidates(...values) {
  const out = [];
  const seen = new Set();
  const push = (v) => {
    const raw = typeof v === "string" ? v.trim() : "";
    if (!raw || seen.has(raw)) return;
    const lower = raw.toLowerCase();
    if (!(lower.startsWith("https://") || lower.startsWith("http://"))) return;
    if (lower.startsWith("data:")) return;
    seen.add(raw);
    out.push(raw);
  };
  for (const value of values) {
    if (Array.isArray(value)) {
      for (const item of value) {
        push(typeof item === "object" && item ? item.url || item.imageUrl || item.src : item);
      }
    } else if (value && typeof value === "object") {
      push(value.url || value.imageUrl || value.src);
    } else {
      push(value);
    }
  }
  return out;
}

function ImagePanel({
  candidates,
  state,
  href,
  badge,
  badgeTone,
  badgeTitle,
  disabled,
}) {
  const list = Array.isArray(candidates) ? candidates : [];
  // Failed URLs are tracked (not an index) so a URL that arrives later during a
  // live update is still tried without resetting the panel.
  const [failed, setFailed] = useState(() => new Set());
  const src = list.find((url) => !failed.has(url)) || null;
  const zoom = useHoverZoom(Boolean(src));

  const inner = (
    <div
      className={cn("pc-image-panel", !src && "pc-image-empty")}
      ref={zoom.ref}
      {...zoom.handlers}
      title={badgeTitle}
      data-image-state={src ? state : `${state}_placeholder`}
    >
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="pc-image"
          style={
            zoom.active
              ? {
                  transform: "scale(2.4)",
                  transformOrigin: `${zoom.pos.x}% ${zoom.pos.y}%`,
                }
              : undefined
          }
          onError={() =>
            setFailed((prev) => {
              if (prev.has(src)) return prev;
              const next = new Set(prev);
              next.add(src);
              return next;
            })
          }
        />
      ) : (
        <NeutralProductIllustration tone={badgeTone} />
      )}
      <div className="pc-image-gutter" aria-hidden="true" />
      <GlassBadge tone={badgeTone} title={badgeTitle}>
        {badge}
      </GlassBadge>
    </div>
  );

  if (!href || disabled) return inner;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="pc-image-link"
      onClick={(e) => e.stopPropagation()}
      title={badgeTitle}
    >
      {inner}
    </a>
  );
}

/**
 * Dual BUY / SELL product image strip.
 *
 * BUY  hierarchy: retailer image → another genuine image of the source product
 *                 → neutral placeholder.
 * SELL hierarchy: Walmart listing image → another validated image of the same
 *                 Walmart item → genuine source image as a visual reference →
 *                 neutral placeholder.
 *
 * A source-image reference on the SELL side is marked `data-image-state="sell_source_reference"`
 * so it is never mistaken for verified Walmart media, and it renders no caption.
 */
export function ProductCardMedia({
  sourceImage,
  sourceImages,
  sourceUrl,
  sourceMerchant,
  walmartImage,
  walmartImages,
  walmartUrl,
  hasWalmart,
}) {
  const buyCandidates = useMemo(
    () => imageCandidates(sourceImage, sourceImages),
    [sourceImage, sourceImages],
  );
  const walmartCandidates = useMemo(
    () => imageCandidates(walmartImage, walmartImages),
    [walmartImage, walmartImages],
  );
  // Stage Y P0-A: the SELL panel must NEVER silently borrow the source image.
  //
  // Previously this chained `[...walmartCandidates, ...buyCandidates]`, so when a
  // Walmart image failed to load (blocked hotlink, 404, expiring CDN token) the
  // panel fell through to the DERMSTORE photo while still rendering the SELL
  // badge and `data-image-state="sell"`. That is how a customer saw an
  // AMELIORATE bottle pictured beside an Avon title and brand: two different
  // products welded into one card with no indication.
  //
  // A source image may appear on the SELL side only when there is NO Walmart
  // media at all, and then it is explicitly tagged `sell_source_reference`.
  const sellHasOwnMedia = walmartCandidates.length > 0;
  const sellCandidates = useMemo(
    () => (sellHasOwnMedia ? [...walmartCandidates] : [...buyCandidates]),
    [sellHasOwnMedia, walmartCandidates, buyCandidates],
  );

  return (
    <div className="pc-media" data-testid="product-card-media">
      <ImagePanel
        candidates={buyCandidates}
        state="source"
        href={sourceUrl}
        badgeTitle={sourceMerchant || "Source"}
        badgeTone="buy"
        badge={
          <>
            <ShoppingCart className="pc-glass-icon" aria-hidden="true" />
            <span>BUY</span>
          </>
        }
      />
      {hasWalmart ? (
        <ImagePanel
          candidates={sellCandidates}
          state={
            sellHasOwnMedia
              ? "sell"
              : buyCandidates.length
                ? "sell_source_reference"
                : "sell"
          }
          href={walmartUrl}
          badgeTitle="Walmart listing"
          badgeTone="sell"
          badge={
            <>
              <WalmartSparkIcon />
              <span>SELL</span>
            </>
          }
        />
      ) : (
        <div
          className="pc-image-panel pc-image-empty pc-no-walmart"
          data-testid="product-card-no-walmart"
          data-image-state="no_walmart_candidate"
        >
          <NeutralProductIllustration tone="sell" />
          <div className="pc-image-gutter" aria-hidden="true" />
          <GlassBadge tone="sell" title="No Walmart match">
            <WalmartSparkIcon />
            <span>SELL</span>
          </GlassBadge>
        </div>
      )}
    </div>
  );
}

export default ProductCardMedia;
